// Time formatting for "Ops on duty today" (GRAVY-10878). With no `timeZone`
// the browser's zone is used: the bar shows shifts in the viewer's time
// (BR-05). Callers pass the ops zone where the admin reads ops-local times.

const parts = (iso: string, timeZone?: string) => {
  const fmt = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });
  const map = Object.fromEntries(
    fmt.formatToParts(new Date(iso)).map((p) => [p.type, p.value]),
  );
  return {
    hour: map.hour,
    minute: map.minute,
    dayPeriod: (map.dayPeriod ?? '').toUpperCase(),
  };
};

/** "6am", "6:30am" */
export const formatCompactTime = (iso: string, timeZone?: string): string => {
  const { hour, minute, dayPeriod } = parts(iso, timeZone);
  return `${hour}${minute === '00' ? '' : `:${minute}`}${dayPeriod.toLowerCase()}`;
};

/** "6am-2pm" (DB-02) */
export const formatCompactRange = (
  startsAt: string,
  endsAt: string,
  timeZone?: string,
): string =>
  `${formatCompactTime(startsAt, timeZone)}-${formatCompactTime(endsAt, timeZone)}`;

/** "6:00 AM" */
export const formatFullTime = (iso: string, timeZone?: string): string => {
  const { hour, minute, dayPeriod } = parts(iso, timeZone);
  return `${hour}:${minute} ${dayPeriod}`;
};

/** "6:00 AM - 2:00 PM" (DB-11) */
export const formatFullRange = (
  startsAt: string,
  endsAt: string,
  timeZone?: string,
  separator = ' - ',
): string =>
  `${formatFullTime(startsAt, timeZone)}${separator}${formatFullTime(endsAt, timeZone)}`;

// "x12", "ext 12", "ext. 12", "extension 12" at the end of the number.
const EXTENSION = /\s*(?:x|ext\.?|extension)\s*(\d+)\s*$/i;

/**
 * A tel: link a phone can dial (DB-15): digits and a leading + only, with an
 * extension kept apart as RFC 3966 `;ext=`. "202-555-0143 x12" gives
 * "tel:2025550143;ext=12", never "tel:202555014312".
 */
export const telHref = (phone: string): string => {
  const ext = EXTENSION.exec(phone);
  const number = (ext ? phone.slice(0, ext.index) : phone).replace(
    /[^\d+]/g,
    '',
  );
  return `tel:${number}${ext ? `;ext=${ext[1]}` : ''}`;
};
